import apiClient from '@contexts/AuthContext/apiClient';
import { usersApis } from '@shared/apis';
import { StudentContactExportRow } from '@shared/apis/users.apis';
import useToast from 'apps/agora/src/hooks/useToast';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';
import qs from 'qs';
import { useState } from 'react';

const buildCsv = (rows: StudentContactExportRow[]) => {
  const mentorColumns = Math.max(0, ...rows.map((row) => row.mentors.length));

  const header = [
    'Student Name',
    'Student Phone',
    'Student Email',
    'Admitted To',
    'Inactive Mentors',
  ];

  for (let index = 1; index <= mentorColumns; index++) {
    header.push(
      `Active Mentor ${index} Name`,
      `Active Mentor ${index} Phone`,
      `Active Mentor ${index} Email`
    );
  }

  const body = rows.map((row) => {
    const cells = [
      row.name,
      row.phone,
      row.email,
      row.admissions.map((admission) => `${admission.university} (${admission.status})`).join('; '),
      String(row.inactiveMentorCount),
    ];

    for (let index = 0; index < mentorColumns; index++) {
      const mentor = row.mentors[index];
      cells.push(mentor?.name ?? '', mentor?.phone ?? '', mentor?.email ?? '');
    }

    return cells;
  });

  return stringify([header, ...body]);
};

const downloadCsv = (csv: string, fileName: string) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const useExportStudentContacts = (searchParams: Record<string, unknown>, total: number) => {
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const exportStudentContacts = async () => {
    if (!total) {
      toast.info('No students match the current filters.');
      return;
    }

    setIsExporting(true);

    try {
      const query = qs.stringify(
        { ...searchParams, page: 1, limit: total },
        { arrayFormat: 'indices' }
      );
      const { data: searchResult } = await apiClient.get(
        `${usersApis.searchStudents.endpoint}?${query}`
      );

      const studentIds: string[] = (searchResult?.students ?? []).map(
        (student: { _id: string }) => student._id
      );

      const { data } = await apiClient.post(usersApis.exportStudentContacts.endpoint, {
        studentIds,
      });

      const rows: StudentContactExportRow[] = data?.rows ?? [];

      downloadCsv(buildCsv(rows), `students-${moment().format('YYYY-MM-DD')}.csv`);
      toast.success(`Exported ${rows.length} student(s).`);
    } catch (error: any) {
      toast.error(error?.response?.data?.error?.message ?? 'The export could not be generated.');
    } finally {
      setIsExporting(false);
    }
  };

  return { exportStudentContacts, isExporting };
};

export default useExportStudentContacts;
